import { useEffect, useRef } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { Customer } from '@/types/customer-type';
import { FbConversation } from '@/types/fb-type';
import { toast } from './use-toast';

export interface UseOrderConversationOptions {
  /** Whether the hook is allowed to run (e.g. wait until order is loaded). Defaults to true. */
  enabled?: boolean;
  /** The order.source value (e.g. 'facebook_COMMENT', 'telegram', 'pos'). */
  orderSource?: string | null;
  /** Whether to show a warning toast if a Facebook order customer has no conversation yet. Defaults to true for order detail. */
  showMissingChatWarning?: boolean;
}

export interface UseOrderConversationReturn {
  isFacebookSource: boolean;
  shouldShowChat: boolean;
  currentConversation: FbConversation | null;
  facebookId: string | null;
  customerName: string | null;
  isInactive: boolean;
}

export function isFacebookOrderOrCustomer(
  orderSource: string | null | undefined,
  customer: Customer | null,
): boolean {
  if (typeof orderSource === 'string' && orderSource.trim().length > 0) {
    return orderSource.toLowerCase().includes('facebook');
  }
  if (!customer) return false;
  return Boolean(
    customer.facebookConversationId ||
      customer.facebookId ||
      customer.saleChannel?.toLowerCase() === 'facebook',
  );
}

export function useOrderConversation(
  customer: Customer | null,
  currentPageId?: string | null,
  options?: UseOrderConversationOptions,
): UseOrderConversationReturn {
  const {
    currentConversation,
    loadingConversations,
    selectConversationById,
    selectConversation,
  } = useChat();

  const enabled = options?.enabled ?? true;
  const orderSource = options?.orderSource;
  const showMissingChatWarning = options?.showMissingChatWarning ?? true;
  const isFacebookSource = isFacebookOrderOrCustomer(orderSource, customer);

  const selectedConversationRef = useRef<string | null>(null);

  useEffect(() => {
    selectedConversationRef.current = null;
  }, [customer?.id]);

  useEffect(() => {
    if (!enabled || !customer || !isFacebookSource) {
      return;
    }

    const customerFacebookId = customer.facebookId;
    const customerConversationId = customer.facebookConversationId;
    const isCustomerFromAnotherPage = Boolean(
      customer.facebookPageId &&
      currentPageId &&
      customer.facebookPageId !== currentPageId,
    );
    const selectionKey = `${isCustomerFromAnotherPage ? 'cache' : 'live'}:${currentPageId ?? ''}:${customerConversationId ?? ''}`;

    if (!customerConversationId) {
      if (
        showMissingChatWarning &&
        !customerFacebookId &&
        selectedConversationRef.current !== customer.id
      ) {
        toast({
          title: 'Warning',
          description: `[OrderDetails] Customer ${customer.id} has not chat with us please inform them to chat with us !`,
          variant: 'warning',
          duration: 0,
        });
        selectedConversationRef.current = customer.id;
      }
      return;
    }

    if (isCustomerFromAnotherPage) {
      if (selectedConversationRef.current !== selectionKey) {
        const cachedConversation = Object.assign(new FbConversation(), {
          id: customerConversationId,
          pageId: customer.facebookPageId,
          cacheOnly: true,
          snippet: '',
          updatedTime: customer.lastFacebookMessage ?? customer.updatedAt,
          messageCount: 0,
          unreadCount: 0,
          participants: [
            {
              id: customerFacebookId || customer.id,
              name: customer.name,
              username: '',
              profileImageUrl: customer.profileImageUrl ?? undefined,
              conversationId: customerConversationId,
            },
          ],
          lastCustomerMessageTime: customer.lastFacebookMessage,
          messagingWindow: 'CLOSED' as const,
        });
        selectedConversationRef.current = selectionKey;
        selectConversation(cachedConversation);
      }
      return;
    }

    if (loadingConversations) {
      return;
    }

    if (selectedConversationRef.current !== selectionKey) {
      selectedConversationRef.current = selectionKey;
      selectConversationById(customerConversationId);
    }
  }, [
    enabled,
    isFacebookSource,
    showMissingChatWarning,
    customer?.id,
    customer?.facebookId,
    customer?.facebookConversationId,
    customer?.facebookPageId,
    customer?.lastFacebookMessage,
    customer?.name,
    customer?.profileImageUrl,
    customer?.updatedAt,
    currentPageId,
    loadingConversations,
    selectConversation,
    selectConversationById,
  ]);

  const shouldShowChat = Boolean(
    enabled &&
    isFacebookSource &&
    currentConversation &&
    customer?.facebookConversationId &&
    currentConversation.id === customer.facebookConversationId,
  );
  const facebookId = isFacebookSource ? customer?.facebookId || null : null;
  const customerName = customer?.name || null;
  const isInactive = Boolean(
    isFacebookSource &&
    customer?.facebookPageId &&
    currentPageId &&
    customer.facebookPageId !== currentPageId,
  );
  return {
    isFacebookSource,
    shouldShowChat,
    currentConversation: isFacebookSource ? currentConversation : null,
    facebookId,
    customerName,
    isInactive,
  };
}

